import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiClient } from '@utils/apiClient';
import { useAuth } from '../../Auth';

export interface DashboardMetric {
  title: string;
  value: string;
  trend: string;
  icon: string;
  trendType: 'positive' | 'negative' | 'neutral';
}

export interface Call {
  id: string;
  time: string;
  caller: string;
  agent: string;
  duration: string;
  status: string;
  direction: string;
  started_at: string;
  ended_at: string;
}

export interface DashboardData {
  metrics: DashboardMetric[];
  recentCalls: Call[];
}

export const useDashboard = () => {
  const { t } = useTranslation();
  const { organization } = useAuth();
  const isInvoiceBilled = Boolean(organization?.is_invoice_billed);
  const [data, setData] = useState<DashboardData>({
    metrics: [],
    recentCalls: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatTimeAgo = useCallback(
    (dateString: string): string => {
      const now = new Date();
      const date = new Date(dateString);

      if (isNaN(date.getTime()) || date.getFullYear() < 2020) {
        return t('common.unknownTime');
      }

      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

      if (diffInMinutes < 1) return t('common.justNow');
      if (diffInMinutes < 60) return t('common.minutesAgo', { count: diffInMinutes });

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return t('common.hoursAgo', { count: diffInHours });

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays > 365) {
        const diffInYears = Math.floor(diffInDays / 365);
        return t('common.yearsAgo', { count: diffInYears });
      }

      return t('common.daysAgo', { count: diffInDays });
    },
    [t]
  );

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await ApiClient.get('/telephony/calls/dashboard/');

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.status}`);
      }

      const apiData = await response.json();

      const metrics: DashboardMetric[] = [
        {
          title: t('dashboard.activeCalls'),
          value: apiData.active_calls_count.toString(),
          trend:
            apiData.active_calls_count > 0
              ? t('dashboard.activeCalls')
              : t('dashboard.noActiveCalls'),
          icon: 'phone',
          trendType: apiData.active_calls_count > 0 ? 'positive' : 'neutral',
        },
        {
          title: t('dashboard.totalCalls'),
          value: apiData.today_calls_count.toString(),
          trend:
            apiData.today_calls_count > 0
              ? t('dashboard.callsCompletedToday')
              : t('dashboard.noRecentCalls'),
          icon: 'phone_callback',
          trendType: apiData.today_calls_count > 0 ? 'positive' : 'neutral',
        },
        {
          title: t('dashboard.averageDuration'),
          value: `${(apiData.avg_call_duration / 60).toFixed(1)} min`,
          trend:
            apiData.avg_call_duration > 0
              ? t('dashboard.averageDuration')
              : t('dashboard.noDataAvailable'),
          icon: 'schedule',
          trendType: apiData.avg_call_duration > 0 ? 'positive' : 'neutral',
        },
        {
          title: t('dashboard.currentBalance'),
          value: `${apiData.wallet_balance.toFixed(2)} €`,
          trend:
            apiData.wallet_balance > 0
              ? t('dashboard.balanceAvailable')
              : t('dashboard.insufficientBalance'),
          icon: 'account_balance_wallet',
          trendType: apiData.wallet_balance > 0 ? 'positive' : 'negative',
        },
      ].filter(m => isInvoiceBilled ? m.icon !== 'account_balance_wallet' : true) as DashboardMetric[];

      const recentCalls: Call[] = apiData.recent_calls.map((call: Record<string, unknown>) => ({
        id: call.id as string,
        time: formatTimeAgo(call.started_at as string),
        caller: call.caller_number as string,
        agent: (call.agent as Record<string, unknown>)?.name as string,
        duration: formatDuration(call.duration as number),
        status: (call.status_display || call.status) as string,
        direction: (call.direction_display || call.direction) as string,
        started_at: call.started_at as string,
        ended_at: call.ended_at as string,
      }));

      setData({ metrics, recentCalls });
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      setLoading(false);
    }
  }, [t, formatTimeAgo]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    data,
    loading,
    error,
  };
};
